import React, { useState } from 'react';
import { userAPI } from '../../../services/api';
import TextInput from '../../Common/TextInput';
import SearchableRoleDropdown from '../../Common/SearchableRoleDropdown';
import AdditionalRolesSelect from './AdditionalRolesSelect';

const UserCreateForm = ({ onSuccess, onCancel }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    primaryRole: '',
    department: '',
    phone: '',
    isActive: true,
    emailVerified: false
  });
  
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [selectedRoleLabel, setSelectedRoleLabel] = useState('');
  const [additionalRoles, setAdditionalRoles] = useState([]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleRoleChange = (roleCode, label) => {
    setFormData(prev => ({
      ...prev,
      primaryRole: roleCode
    }));
    setSelectedRoleLabel(label);
    setAdditionalRoles((prev) =>
      prev.filter((code) => code.toUpperCase() !== (roleCode || '').toUpperCase())
    );

    if (errors.primaryRole) {
      setErrors(prev => ({
        ...prev,
        primaryRole: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.password.trim()) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters long';
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.password)) {
      newErrors.password = 'Password must contain uppercase, lowercase, and number';
    }

    if (!formData.confirmPassword.trim()) {
      newErrors.confirmPassword = 'Please confirm password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    if (!formData.primaryRole) {
      newErrors.primaryRole = 'Role is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      const userData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        password: formData.password,
        primaryRole: formData.primaryRole,
        additionalRoles,
        department: formData.department,
        phone: formData.phone,
        isActive: formData.isActive,
        emailVerified: formData.emailVerified
      };
      
      await userAPI.createUser(userData);
      onSuccess && onSuccess();
    } catch (error) {
      console.error('Failed to create user:', error);
      setErrors({ 
        submit: error.response?.data?.message || 'Failed to create user. Please try again.' 
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 backdrop-blur-sm bg-black/20 z-50 flex items-center justify-center p-2 sm:p-4">
      <div className="relative w-full max-w-4xl max-h-[95vh] bg-white rounded-lg shadow-2xl border border-gray-100 flex flex-col overflow-hidden">
        {/* Compact Header */}
        <div className="flex-shrink-0 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-t-lg px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
                <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-white">Create New User</h3>
            </div>
            <button
              onClick={onCancel}
              className="w-7 h-7 rounded-lg flex items-center justify-center text-white/80 hover:text-white hover:bg-white/20 transition-all"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {errors.submit && (
            <div className="bg-gradient-to-r from-red-50 to-pink-50 border border-red-200 rounded-lg p-3">
              <div className="flex items-center">
                <svg className="h-4 w-4 text-red-600 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-sm font-medium text-red-800">{errors.submit}</p>
              </div>
            </div>
          )}

            {/* Main Fields Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Left Column */}
              <div className="space-y-4">
                {/* Personal Information */}
                <TextInput
                  variant="input"
                  label="First Name"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  placeholder="Enter first name"
                  error={errors.firstName}
                  required
                />

                <TextInput
                  variant="input"
                  label="Last Name"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  placeholder="Enter last name"
                  error={errors.lastName}
                  required
                />

                {/* Email */}
                <TextInput
                  variant="input"
                  type="email"
                  label="Email Address"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="user@company.com"
                  error={errors.email}
                  required
                />

                {/* Phone */}
                <TextInput
                  variant="input"
                  type="tel"
                  label="Phone Number"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="+1 (555) 000-0000"
                />
              </div>

              {/* Right Column */}
              <div className="space-y-4">
                {/* Password Fields */}
                <TextInput
                  variant="input"
                  type="password"
                  label="Initial Password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Min 8 characters"
                  error={errors.password}
                  required
                  helperText="Must contain uppercase, lowercase, and number"
                />

                <TextInput
                  variant="input"
                  type="password"
                  label="Confirm Password"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  placeholder="Confirm password"
                  error={errors.confirmPassword}
                  required
                />

                {/* Role Selection */}
                <div>
                  <label htmlFor="primaryRole" className="block text-sm font-semibold text-gray-700 mb-2">
                    Primary role *
                  </label>
                  <SearchableRoleDropdown
                    value={formData.primaryRole}
                    onChange={handleRoleChange}
                    displayLabel={selectedRoleLabel}
                    placeholder="Search and select a role"
                    error={!!errors.primaryRole}
                  />
                  {errors.primaryRole && (
                    <p className="mt-2 text-sm text-red-600 flex items-center">
                      <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      {errors.primaryRole}
                    </p>
                  )}
                  <p className="mt-1 text-xs text-gray-500 flex items-center">
                    <svg className="w-3 h-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Default role for login and reporting
                  </p>
                </div>

                <AdditionalRolesSelect
                  primaryRole={formData.primaryRole}
                  value={additionalRoles}
                  onChange={setAdditionalRoles}
                />

                {/* Department */}
                <TextInput
                  variant="input"
                  label="Department"
                  name="department"
                  value={formData.department}
                  onChange={handleChange}
                  placeholder="Engineering, Sales, HR, etc."
                />
              </div>
            </div>

            {/* Status Toggle */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <input
                    id="isActive"
                    name="isActive"
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={handleChange}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  />
                  <div>
                    <label htmlFor="isActive" className="block text-sm font-semibold text-gray-900">
                      Active User
                    </label>
                    <p className="text-xs text-gray-500">User can log in immediately</p>
                  </div>
                </div>
                <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                  formData.isActive 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-gray-100 text-gray-600'
                }`}>
                  <div className={`w-1.5 h-1.5 rounded-full mr-1.5 ${
                    formData.isActive ? 'bg-green-500' : 'bg-gray-400'
                  }`}></div>
                  {formData.isActive ? 'Active' : 'Inactive'}
                </div>
              </div>
            </div>

            {/* Compact Preview */}
            {formData.email && (
              <div className="bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-lg p-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <svg className="w-4 h-4 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                    <span className="text-sm font-semibold text-indigo-900">Preview:</span>
                  </div>
                  {formData.primaryRole && (
                    <div className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                      <div className="w-1.5 h-1.5 rounded-full mr-1.5 bg-indigo-500"></div>
                      {selectedRoleLabel || formData.primaryRole}
                    </div>
                  )}
                </div>
                <div className="mt-2">
                  <div className="font-medium text-sm text-indigo-800">
                    {formData.firstName} {formData.lastName}
                  </div>
                  <div className="text-xs text-indigo-700 mt-1">{formData.email}</div>
                  {formData.department && (
                    <div className="text-xs text-indigo-600 mt-0.5">{formData.department}</div>
                  )}
                </div>
              </div>
            )}
          </div>

            {/* Form Actions — sticky footer so Cancel/Create stay visible on short screens */}
            <div className="flex-shrink-0 flex justify-end gap-3 px-4 py-3 border-t border-gray-100 bg-white">
              <button
                type="button"
                onClick={onCancel}
                className="px-4 py-2 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 transition-all"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 border border-transparent rounded-lg text-sm font-medium text-white bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                {loading ? (
                  <div className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-2 h-3 w-3 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Creating...
                  </div>
                ) : (
                  <div className="flex items-center">
                    <svg className="w-3 h-3 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Create
                  </div>
                )}
              </button>
            </div>
          </form>
      </div>
    </div>
  );
};

export default UserCreateForm;